import type { Scan } from "@hobbyshopify/backend-client";
import { formatCents, scanImage, scanRowSubtitle, scanTitle } from "../core/primitives/card-display";
import { quotedCents } from "../core/primitives/customer-cart";
import { formatDate } from "../core/primitives/format";

function Thumb({ src }: { src: string | null }) {
  return (
    <span className="block h-12 w-12 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
      {src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-xs text-faint">No img</span>
      )}
    </span>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-4 w-4" aria-hidden="true">
      <path d="M4 6h12M8 6V4.5h4V6m-6.5 0 .7 9.5h7.6l.7-9.5M8.5 9v4.5M11.5 9v4.5" strokeLinecap="round" />
    </svg>
  );
}

/** A customer's confirmed scan, as their cart sees it. */
export interface ScanCartControls {
  readonly inCart: boolean;
  /** In the cart, but left out of the purchase order. */
  readonly excluded: boolean;
  readonly canAdd: boolean;
  /** The cart is read, so Remove knows whether it takes the line or the whole cart. */
  readonly canRemove: boolean;
  /** An add or a remove is on its way. */
  readonly busy: boolean;
  onAdd(): void;
  onRemove(): void;
}

/**
 * A customer's confirmed scan, as iOS's `AdminScanRow`: what it is quoted at,
 * where it stands with their cart, and Open, the cart and Delete. The row
 * itself opens it too.
 */
export function CustomerScanRow({
  scan,
  cart,
  deleting,
  onOpen,
  onDelete,
}: {
  scan: Scan;
  cart: ScanCartControls;
  deleting: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const card = scan.card;
  const title = scanTitle(scan);
  const subtitle = scanRowSubtitle(scan);
  const busy = cart.busy || deleting;
  return (
    <div className="rounded-row border border-line bg-surface p-3">
      <button type="button" onClick={onOpen} aria-label={`Open ${title}`} className="flex w-full gap-3 text-left">
        <Thumb src={scanImage(scan)} />
        <span className="block min-w-0 flex-1">
          <span className="flex items-start justify-between gap-2">
            <span className="line-clamp-2 text-sm font-medium text-white">{title}</span>
            <span className="shrink-0 text-xs">
              {cart.inCart ? (
                cart.excluded ? (
                  <span className="text-muted">In cart · excluded</span>
                ) : (
                  <span className="font-semibold text-accent">In cart</span>
                )
              ) : scan.quoteState === "expired" ? (
                <span className="text-warn">Quote expired</span>
              ) : scan.quoteState === "sold" ? (
                <span className="text-muted">Sold</span>
              ) : (
                <span className="text-faint">{formatDate(scan.createdAt)}</span>
              )}
            </span>
          </span>
          <span className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="text-[15px] font-semibold text-accent">{formatCents(quotedCents(scan))}</span>
            {card?.trackStock &&
              (card.available > 0 ? (
                <span className="text-xs text-accent">{card.available} in stock</span>
              ) : (
                <span className="text-xs text-warn">Out of stock</span>
              ))}
            {/* The shop quoted a card it holds none of: the merchant's to check before handing it over. */}
            {scan.heldCopy === false && <span className="text-xs text-warn">No copy held</span>}
          </span>
          {subtitle && <span className="mt-0.5 block truncate text-xs text-faint">{subtitle}</span>}
        </span>
        <span className="self-center text-lg text-faint" aria-hidden="true">
          ›
        </span>
      </button>
      <div className="mt-2.5 flex items-center gap-2 pl-[60px]">
        <button type="button" onClick={onOpen} className="btn-secondary btn-xs">
          Open
        </button>
        {cart.inCart ? (
          <button
            type="button"
            onClick={cart.onRemove}
            disabled={busy || !cart.canRemove}
            className="btn-ghost btn-xs text-danger"
          >
            {cart.busy ? "Removing…" : "Remove"}
          </button>
        ) : (
          cart.canAdd && (
            <button type="button" onClick={cart.onAdd} disabled={busy} className="btn-primary btn-xs">
              {cart.busy ? "Adding…" : "Add to cart"}
            </button>
          )
        )}
        <button
          type="button"
          onClick={onDelete}
          disabled={busy}
          aria-label="Delete scan"
          className="ml-auto flex h-8 min-w-8 items-center justify-center rounded-btn px-2 text-xs text-danger hover:bg-danger/10 disabled:opacity-50"
        >
          {deleting ? "Deleting…" : <TrashIcon />}
        </button>
      </div>
    </div>
  );
}
